import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { CATEGORIES, LANGUAGES } from '../data/shayari';
import Logo from './Logo';

const Navbar = () => {
  const { language, setLanguage, theme, toggleTheme, searchQuery, setSearchQuery, favorites } = useApp();
  const [menuOpen, setMenuOpen]     = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate  = useNavigate();
  const location  = useLocation();

  // Scroll detection
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu + search on route change
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close on ESC key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); setSearchOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__inner">
          {/* Logo */}
          <div className="navbar__logo" onClick={closeMenu}>
            <Logo size="md" linked={true} />
          </div>

          {/* Desktop Nav Links */}
          <div className="navbar__links">
            <Link to="/" className={`navbar__link ${location.pathname === '/' ? 'active' : ''}`}>
              {language==='ur'?'مرکزی':language==='hi'?'मुख्य पृष्ठ':language==='es'?'Inicio':language==='fr'?'Accueil':'Home'}
            </Link>
            <div className="navbar__dropdown-wrap">
              <button className="navbar__link navbar__link--btn">
                {language==='ur'?'زمرے':language==='hi'?'श्रेणियाँ':language==='es'?'Categorías':language==='fr'?'Catégories':'Categories'} <span className="navbar__caret">▾</span>
              </button>
              <div className="navbar__dropdown">
                <div className="navbar__dropdown-grid">
                  {CATEGORIES.map(cat => (
                    <Link key={cat.id} to={`/category/${cat.id}`} className="navbar__dropdown-item">
                      <span className="navbar__dropdown-icon">{cat.icon}</span>
                      <span>{cat.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link to="/poets" className={`navbar__link ${location.pathname === '/poets' ? 'active' : ''}`}>
              {language==='ur'?'شاعر':language==='hi'?'शायर':language==='es'?'Poetas':language==='fr'?'Poètes':'Poets'}
            </Link>
            <Link to="/favorites" className={`navbar__link ${location.pathname === '/favorites' ? 'active' : ''}`}>
              {language==='ur'?'پسندیدہ':language==='hi'?'पसंदीदा':language==='es'?'Favoritos':language==='fr'?'Favoris':'Favorites'}
              {favorites.length > 0 && <span className="navbar__badge">{favorites.length}</span>}
            </Link>
            <Link to="/poster" className={`navbar__link navbar__link--poster ${location.pathname === '/poster' ? 'active' : ''}`}>
              {language==='ur'?'🎨 پوسٹر':language==='hi'?'🎨 पोस्टर':language==='es'?'🎨 Póster':language==='fr'?'🎨 Affiche':'🎨 Poster'}
            </Link>
          </div>

          {/* Right Controls */}
          <div className="navbar__controls">
            {/* Search toggle */}
            <button className="navbar__icon-btn" onClick={() => setSearchOpen(s => !s)} title="Search" aria-label="Search">
              🔍
            </button>

            {/* Language Switcher — desktop only */}
            <div className="lang-switcher">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.id}
                  className={`lang-btn ${language === lang.id ? 'lang-btn--active' : ''}`}
                  onClick={() => setLanguage(lang.id)}
                  title={lang.name}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Theme toggle */}
            <button className="navbar__icon-btn theme-btn" onClick={toggleTheme} title="Toggle theme" aria-label="Toggle theme">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {/* Hamburger — mobile only */}
            <button
              className={`hamburger ${menuOpen ? 'hamburger--open' : ''}`}
              onClick={() => setMenuOpen(s => !s)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* Expandable Search Bar */}
        <div className={`navbar__search-bar ${searchOpen ? 'navbar__search-bar--open' : ''}`}>
          <form onSubmit={handleSearch} className="navbar__search-form">
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={language==='ur'?'شاعری، شاعر، زمرہ تلاش کریں…':language==='hi'?'शायरी, शायर, श्रेणी खोजें…':language==='es'?'Buscar shayari, poeta, categoría…':language==='fr'?'Rechercher shayari, poète, catégorie…':'Search shayari, poet, category…'}
              className="navbar__search-input"
              aria-label="Search shayari"
            />
            <button type="submit" className="navbar__search-submit">
              {language==='ur'?'تلاش':language==='hi'?'खोजें':language==='es'?'Buscar':language==='fr'?'Chercher':'Search'}
            </button>
            <button type="button" className="navbar__search-close" onClick={() => { setSearchOpen(false); setSearchQuery(''); }} aria-label="Close search">✕</button>
          </form>
        </div>
      </nav>

      {/* Backdrop overlay — tap to close menu */}
      <div
        className={`mobile-overlay ${menuOpen ? 'mobile-overlay--visible' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile Slide-in Menu */}
      <div
        id="mobile-menu"
        className={`mobile-menu ${menuOpen ? 'mobile-menu--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="mobile-menu__inner">

          {/* Language + Theme row */}
          <div className="mobile-menu__top-row">
            <div className="mobile-menu__lang">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.id}
                  className={`lang-btn ${language === lang.id ? 'lang-btn--active' : ''}`}
                  onClick={() => setLanguage(lang.id)}
                  title={lang.name}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <button className="navbar__icon-btn theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>

          {/* Main nav links */}
          <Link to="/" className="mobile-menu__link" onClick={closeMenu}>🏠 {language==='ur'?'مرکزی':language==='hi'?'मुख्य पृष्ठ':language==='es'?'Inicio':language==='fr'?'Accueil':'Home'}</Link>
          <Link to="/poets" className="mobile-menu__link" onClick={closeMenu}>📜 {language==='ur'?'شاعر':language==='hi'?'शायर':language==='es'?'Poetas':language==='fr'?'Poètes':'Poets'}</Link>
          <Link to="/favorites" className="mobile-menu__link" onClick={closeMenu}>
            ❤️ {language==='ur'?'پسندیدہ':language==='hi'?'पसंदीदा':language==='es'?'Favoritos':language==='fr'?'Favoris':'Favorites'}
            {favorites.length > 0 && <span className="navbar__badge" style={{marginLeft:'auto'}}>{favorites.length}</span>}
          </Link>
          <Link to="/poster" className="mobile-menu__link" onClick={closeMenu}>🎨 {language==='ur'?'شاعری پوسٹر':language==='hi'?'शायरी पोस्टर':language==='es'?'Póster Shayari':language==='fr'?'Affiche Shayari':'Shayari Poster'}</Link>
          <Link to="/search" className="mobile-menu__link" onClick={closeMenu}>🔍 {language==='ur'?'تلاش':language==='hi'?'खोज':language==='es'?'Buscar':language==='fr'?'Rechercher':'Search'}</Link>
          <Link to="/about" className="mobile-menu__link" onClick={closeMenu}>ℹ️ {language==='ur'?'ہمارے بارے میں':language==='hi'?'हमारे बारे में':language==='es'?'Acerca de':language==='fr'?'À propos':'About Us'}</Link>
          <Link to="/contact" className="mobile-menu__link" onClick={closeMenu}>📩 {language==='ur'?'رابطہ کریں':language==='hi'?'संपर्क करें':language==='es'?'Contacto':language==='fr'?'Contact':'Contact Us'}</Link>
          <Link to="/privacy-policy" className="mobile-menu__link" onClick={closeMenu}>🔒 {language==='ur'?'رازداری پالیسی':language==='hi'?'गोपनीयता नीति':language==='es'?'Privacidad':language==='fr'?'Confidentialité':'Privacy Policy'}</Link>
          <Link to="/terms" className="mobile-menu__link" onClick={closeMenu}>📋 {language==='ur'?'شرائط':language==='hi'?'नियम व शर्तें':language==='es'?'Términos':language==='fr'?'Conditions':'Terms'}</Link>

          {/* Categories */}
          <div className="mobile-menu__divider">
            {language==='ur'?'زمرے':language==='hi'?'श्रेणियाँ':language==='es'?'Categorías':language==='fr'?'Catégories':'Categories'}
          </div>
          <div className="mobile-menu__cat-grid">
            {CATEGORIES.map(cat => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="mobile-menu__cat-chip"
                onClick={closeMenu}
              >
                <span>{cat.icon}</span>
                <span className="mobile-menu__cat-label">{cat.label}</span>
              </Link>
            ))}
          </div>

        </div>
      </div>
    </>
  );
};

export default Navbar;
